import React from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { useOrganizations } from '../../../core/hooks/useOrganizations';
import { UserRole } from '../../../core/models/User';
import type { User } from '../../../core/models/User';

interface UserManagementDialogProps {
  open: boolean;
  onClose: () => void;
  user?: Partial<User>;
  onSubmit: (data: any) => Promise<void>;
}

export const UserManagementDialog: React.FC<UserManagementDialogProps> = ({
  open,
  onClose,
  user,
  onSubmit,
}) => {
  const { organizations } = useOrganizations();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      email: user?.email || '',
      password: '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      displayName: user?.displayName || '',
      phoneNumber: user?.phoneNumber || '',
      role: user?.role || UserRole.PROJECT_ADMIN,
      organizationId: user?.organizationId || '',
    },
  });
  const selectedRole = watch('role');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{user ? 'Edit User' : 'Create User'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Email"
            type="email"
            {...register('email', { required: 'Email is required' })}
            error={errors.email?.message}
          />
          {!user && (
            <Input
              label="Password"
              type="password"
              {...register('password', { 
                required: user ? false : 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' }
              })}
              error={errors.password?.message}
            />
          )}
          <Input
            label="First Name"
            {...register('firstName', { required: 'First name is required' })}
            error={errors.firstName?.message}
          />
          <Input
            label="Last Name"
            {...register('lastName', { required: 'Last name is required' })}
            error={errors.lastName?.message}
          />
          <Input
            label="Display Name"
            {...register('displayName')}
          />
          <Input
            label="Phone Number"
            type="tel"
            {...register('phoneNumber')}
          />
          <div className="space-y-2">
            <label className="text-sm font-medium">Role</label>
            <Select
              value={watch('role')}
              onValueChange={(value) => {
                const role = value as UserRole;
                setValue('role', role);
                if (role === UserRole.SUPER_ADMIN) {
                  setValue('organizationId', '');
                }
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UserRole.PROJECT_ADMIN}>Project Admin</SelectItem>
                <SelectItem value={UserRole.CLIENT_ADMIN}>Client Admin</SelectItem>
                <SelectItem value={UserRole.SUPER_ADMIN}>Super Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {selectedRole !== UserRole.SUPER_ADMIN && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Client</label>
              <Select
                value={watch('organizationId')}
                onValueChange={(value) => setValue('organizationId', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {user ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
